import { getAIQueue } from '../../core/queue/ai-queue.js'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('AIService')

/**
 * AI Service
 * 
 * Handles job queueing and status retrieval for AI operations
 * Actual processing happens in ai.worker.ts to avoid circular dependencies
 */
export class AIService {
  private createFallbackJob(idHint: string) {
    return {
      id: idHint,
      returnvalue: null,
      finishedOn: Date.now(),
      attemptsMade: 0,
      progress: () => 0,
      getState: async () => 'waiting'
    }
  }

  async queuePOISuggestion(data: {
    userId: string
    userTier?: 'free' | 'standard' | 'premium' | 'pro'
    location: { lat: number; lng: number }
    categories: string[]
    radiusKm: number
    preferences?: any
    context?: any
    cacheKey?: string
    planId?: string
    stageId?: string
    planName?: string
    stageName?: string
  }): Promise<string> {
    logger.info('Queueing POI suggestion job', { 
      userId: data.userId,
      location: data.location,
      categories: data.categories,
      radiusKm: data.radiusKm
    })

    const job = await this.enqueueSafely('suggest-pois', data, {
      attempts: 3,
      backoff: { type: 'exponential', delay: 2000 },
      priority: 2, // Normal priority
      timeout: 60000 // 60 seconds (increased for 15-20 POIs with verification)
    })

    logger.info('POI suggestion job queued', { 
      jobId: job.id,
      userId: data.userId
    })

    return String(job.id)
  }

  async queuePlanEnhancement(data: {
    userId: string
    planId: string
    stops?: any[]
    preferences?: any
    constraints?: any
  }): Promise<string> {
    logger.info('Queueing plan enhancement job', { 
      userId: data.userId,
      planId: data.planId,
      stopsCount: data.stops?.length || 0
    })

    const job = await this.enqueueSafely('enhance-plan', data, {
      attempts: 2,
      backoff: { type: 'exponential', delay: 3000 },
      priority: 1, // Higher priority
      timeout: 60000 // 60 seconds
    })

    logger.info('Plan enhancement job queued', { 
      jobId: job.id,
      userId: data.userId,
      planId: data.planId
    })

    return String(job.id)
  }

  async getJobStatus(jobId: string) {
    logger.debug('Fetching AI job status', { jobId })

    const queue = getAIQueue()
    const job = typeof queue.getJob === 'function' ? await queue.getJob(jobId) : null
    if (!job) {
      logger.warn('AI job not found', { jobId })
      return {
        id: jobId,
        state: 'missing',
        progress: 0,
        attemptsMade: 0
      }
    }

    const state = await job.getState()
    logger.debug('AI job state retrieved', { jobId, state })
    
    if (state === 'completed') {
      logger.info('AI job completed', { jobId, completedAt: job.finishedOn })
      return {
        id: jobId,
        state: 'completed',
        result: job.returnvalue,
        completedAt: job.finishedOn
      }
    }

    if (state === 'failed') {
      logger.warn('AI job failed', { jobId, error: job.failedReason, failedAt: job.finishedOn })
      return {
        id: jobId,
        state: 'failed',
        error: job.failedReason,
        failedAt: job.finishedOn
      }
    }

    logger.debug('AI job in progress', { jobId, state, progress: job.progress(), attempts: job.attemptsMade })
    return {
      id: jobId,
      state,
      progress: job.progress(),
      attemptsMade: job.attemptsMade
    }
  }

  async getQueueStatus() {
    logger.debug('Fetching AI queue status')

    let counts = {
      waiting: 0,
      active: 0,
      completed: 0,
      failed: 0,
      delayed: 0,
      paused: 0
    }

    try {
      const queue = getAIQueue()
      if (typeof queue.getJobCounts === 'function') {
        const jobCounts = await queue.getJobCounts()
        counts = { ...counts, ...jobCounts }
      }
    } catch (error) {
      logger.warn('Failed to fetch AI queue counts; using defaults', { error })
    }

    logger.debug('AI queue counts retrieved', { counts })
    
    // Determine health based on queue metrics
    let health: 'healthy' | 'degraded' | 'unhealthy' = 'healthy'
    
    if (counts.failed > 10) {
      logger.warn('AI queue degraded: high failed count', { failedCount: counts.failed })
      health = 'degraded'
    }
    
    if (counts.failed > 50 || counts.waiting > 100) {
      logger.error('AI queue unhealthy', { failedCount: counts.failed, waitingCount: counts.waiting })
      health = 'unhealthy'
    }
    
    logger.info('AI queue status', { 
      waiting: counts.waiting,
      active: counts.active,
      completed: counts.completed,
      failed: counts.failed,
      health
    })
    
    return {
      waiting: counts.waiting,
      active: counts.active,
      completed: counts.completed,
      failed: counts.failed,
      health
    }
  }

  private async enqueueSafely(
    jobName: string,
    payload: Record<string, any>,
    options: Record<string, any>
  ) {
    const queue = getAIQueue()
    if (typeof queue.add !== 'function') {
      return this.createFallbackJob(`${jobName}-pending`)
    }

    try {
      const job = await queue.add(jobName, payload, options)
      if (!job?.id) {
        return this.createFallbackJob(`${jobName}-pending`)
      }
      return job
    } catch (error) {
      logger.warn('Queueing job failed, using fallback', { jobName, error })
      return this.createFallbackJob(`${jobName}-pending`)
    }
  }
}
